import type { EnrichmentAttributeGroup, IngestMode } from './types';

/**
 * Panel state. `selected` and `memory` are keyed by attribute key, `expanded` by
 * group slug — never by API id, which is per-environment.
 */
export interface EnrichmentRunPanelState {
  /** Normalized taxonomy currently rendered. Empty until HYDRATE. */
  groups: EnrichmentAttributeGroup[];
  name: string;
  /** Attribute key → selected. Every known key is present, on or off. */
  selected: Record<string, boolean>;
  /** Group slug → expanded. Groups start expanded. */
  expanded: Record<string, boolean>;
  search: string;
  scoring: { before: boolean; after: boolean };
  /** Bypass cached enrichment and regenerate every product fresh. Off by default. */
  overrideCache: boolean;
  /** Curation's ingest scope. Defaults to the narrower `net_new`; only read by
   *  surfaces that render the control. */
  ingestMode: IngestMode;
  /**
   * Group slug → the attribute keys that were selected when the group locked.
   * Restored when its requirement comes back on, so toggling `description` off
   * and on returns the operator to their selection instead of to zero.
   *
   * Panel-lifetime only — it is not persisted, so closing the panel forgets it.
   */
  memory: Record<string, string[]>;
}

export type EnrichmentRunPanelAction =
  | { type: 'HYDRATE'; groups: EnrichmentAttributeGroup[] }
  | { type: 'TOGGLE_ATTRIBUTE'; key: string }
  | { type: 'TOGGLE_GROUP'; slug: string }
  | { type: 'TOGGLE_EXPAND'; slug: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'SELECT_ALL' }
  | { type: 'SET_SEARCH'; search: string }
  | { type: 'TOGGLE_SCORING'; phase: 'before' | 'after' }
  | { type: 'TOGGLE_OVERRIDE_CACHE' }
  | { type: 'SET_INGEST_MODE'; mode: IngestMode }
  | { type: 'SET_NAME'; name: string };

/** Fresh state before the taxonomy arrives. */
export const INITIAL_PANEL_STATE: EnrichmentRunPanelState = {
  groups: [],
  name: '',
  selected: {},
  expanded: {},
  search: '',
  scoring: { before: false, after: false },
  overrideCache: false,
  ingestMode: 'net_new',
  memory: {},
};

/** Is the whole group locked — either unsupported on this surface, or with its
 *  requirement unmet? `unavailable` is checked first because it can never be
 *  satisfied, so no requirement lookup can unlock it. */
export function isGroupLocked(state: EnrichmentRunPanelState, group: EnrichmentAttributeGroup) {
  if (group.unavailable === true) return true;
  return group.requiresKey !== undefined && !state.selected[group.requiresKey];
}

/**
 * Is one attribute locked? True when its own group is locked, or when the
 * attribute it depends on is unselected. A dependency naming a key this tenant's
 * taxonomy doesn't publish resolves to unselected, which locks the dependent
 * rather than throwing.
 */
export function isAttributeLocked(
  state: EnrichmentRunPanelState,
  group: EnrichmentAttributeGroup,
  requiresKey: string | undefined,
) {
  if (isGroupLocked(state, group)) return true;
  return requiresKey !== undefined && !state.selected[requiresKey];
}

/**
 * The one place dependency rules are applied. Called after every mutating
 * action, so no component and no other reducer branch reasons about locking.
 *
 * Two passes, in order: gated groups first (a locked group banks its selection
 * into `memory` and empties itself), then gated attributes (a dependent whose
 * requirement went off is cleared). Group before attribute matters — clearing a
 * group can remove the requirement a dependent in another group was relying on.
 *
 * Pure and exported on its own so the rules can be exercised without rendering.
 */
export function cascade(state: EnrichmentRunPanelState): EnrichmentRunPanelState {
  const selected = { ...state.selected };
  const memory = { ...state.memory };

  for (const group of state.groups) {
    const unavailable = group.unavailable === true;
    const requirementUnmet = group.requiresKey !== undefined && !selected[group.requiresKey];
    if (!unavailable && !requirementUnmet) continue;
    // An unavailable group can never unlock, so banking its selection would be a
    // bank nothing ever withdraws from — only gated groups get one.
    if (!unavailable) {
      const held = group.attributes.filter((a) => selected[a.key]).map((a) => a.key);
      // Only overwrite the bank when there is something to bank — a group that
      // locks while already empty must not erase an earlier selection.
      if (held.length > 0) memory[group.slug] = held;
    }
    for (const attribute of group.attributes) selected[attribute.key] = false;
  }

  for (const group of state.groups) {
    for (const attribute of group.attributes) {
      if (attribute.requiresKey === undefined) continue;
      if (!selected[attribute.requiresKey]) selected[attribute.key] = false;
    }
  }

  // Single-choice groups: collapse any accidental multi-selection (e.g. the
  // delivery picker seeds every attribute on) down to the first selected option
  // in taxonomy order, so a radio group never shows two filled dots.
  for (const group of state.groups) {
    if (!group.singleSelect) continue;
    let kept = false;
    for (const attribute of group.attributes) {
      if (!selected[attribute.key]) continue;
      if (kept) selected[attribute.key] = false;
      else kept = true;
    }
  }

  // Mandatory attributes can never be off, whatever the gates above did — runs
  // last so it wins over every other pass.
  for (const group of state.groups) {
    for (const attribute of group.attributes) {
      if (attribute.mandatory) selected[attribute.key] = true;
    }
  }

  return { ...state, selected, memory };
}

/**
 * Restores a group's banked selection and clears the bank. Applied when the
 * attribute a gated group depends on is switched back on; `cascade` runs
 * afterwards, so a banked entry that is no longer valid still gets dropped.
 */
function restoreGroup(state: EnrichmentRunPanelState, slug: string): EnrichmentRunPanelState {
  const banked = state.memory[slug];
  if (banked === undefined) return state;
  const selected = { ...state.selected };
  for (const key of banked) selected[key] = true;
  // Rebuilt without the restored slug rather than deleted, so the bank is a plain
  // value the reducer replaces instead of a mutated object.
  const memory = Object.fromEntries(Object.entries(state.memory).filter(([key]) => key !== slug));
  return { ...state, selected, memory };
}

/**
 * Panel reducer. Every mutating branch returns `cascade(next)` so an invalid
 * combination cannot exist even for one render.
 */
export function enrichmentRunPanelReducer(
  state: EnrichmentRunPanelState,
  action: EnrichmentRunPanelAction,
): EnrichmentRunPanelState {
  switch (action.type) {
    case 'HYDRATE': {
      // Re-hydrating with the same taxonomy would wipe the operator's work, so
      // an identical group/attribute set is a no-op.
      if (isSameTaxonomy(state.groups, action.groups)) return state;
      const selected: Record<string, boolean> = {};
      const expanded: Record<string, boolean> = {};
      for (const group of action.groups) {
        expanded[group.slug] = true;
        for (const attribute of group.attributes)
          selected[attribute.key] = attribute.defaultSelected;
      }
      return cascade({ ...state, groups: action.groups, selected, expanded, memory: {} });
    }

    case 'TOGGLE_ATTRIBUTE': {
      const group = state.groups.find((g) => g.attributes.some((a) => a.key === action.key));
      if (!group) return state;
      const attribute = group.attributes.find((a) => a.key === action.key);
      if (!attribute) return state;
      // Mandatory attributes are always on and cannot be toggled off.
      if (attribute.mandatory) return state;
      if (isAttributeLocked(state, group, attribute.requiresKey)) return state;

      // Single-choice group: selecting an option turns it on and every sibling
      // off (radio). Re-selecting the current option is a no-op — a radio can't
      // be cleared back to nothing by clicking it.
      if (group.singleSelect) {
        if (state.selected[action.key]) return state;
        const selected = { ...state.selected };
        for (const sibling of group.attributes) selected[sibling.key] = sibling.key === action.key;
        return cascade({ ...state, selected });
      }

      const nextOn = !state.selected[action.key];
      let next: EnrichmentRunPanelState = {
        ...state,
        selected: { ...state.selected, [action.key]: nextOn },
      };

      // Switching this attribute on may unlock a gated group that was banking a
      // selection — give it back before cascading.
      if (nextOn) {
        for (const candidate of state.groups) {
          if (candidate.requiresKey === action.key) next = restoreGroup(next, candidate.slug);
        }
      }

      return cascade(next);
    }

    case 'TOGGLE_GROUP': {
      const group = state.groups.find((g) => g.slug === action.slug);
      // Single-choice groups have no select-all — the header checkbox is hidden.
      if (!group || isGroupLocked(state, group) || group.singleSelect) return state;
      // Select-all only when the group isn't already fully on. Attributes whose
      // own requirement stays unmet are cleared by the cascade below, so this
      // can never turn on a dependent without its requirement.
      const turnOn = !group.attributes.every((a) => state.selected[a.key]);
      const selected = { ...state.selected };
      // Deselecting a group leaves its mandatory attributes on — the cascade
      // would force them back anyway, but keeping them here avoids a flash.
      for (const attribute of group.attributes)
        selected[attribute.key] = turnOn || !!attribute.mandatory;
      return cascade({ ...state, selected });
    }

    case 'TOGGLE_EXPAND':
      return {
        ...state,
        expanded: { ...state.expanded, [action.slug]: !state.expanded[action.slug] },
      };

    case 'CLEAR_ALL': {
      // Everything off except mandatory attributes, which can never be cleared.
      const selected: Record<string, boolean> = {};
      for (const group of state.groups)
        for (const attribute of group.attributes) selected[attribute.key] = !!attribute.mandatory;
      // Memory goes with it — "clear all" that silently restores a banked set on
      // the next toggle would not have cleared anything.
      return cascade({ ...state, selected, memory: {} });
    }

    case 'SELECT_ALL': {
      // Turn everything on. A single-choice (radio) group can't be "all on", so
      // keep its current pick — or default to the first option when nothing is
      // selected — instead of letting the cascade collapse it and lose the choice.
      const selected: Record<string, boolean> = {};
      for (const group of state.groups) {
        if (group.singleSelect) {
          const current = group.attributes.find((a) => state.selected[a.key]);
          const chosenKey = (current ?? group.attributes[0])?.key;
          for (const attribute of group.attributes)
            selected[attribute.key] = attribute.key === chosenKey;
        } else {
          for (const attribute of group.attributes) selected[attribute.key] = true;
        }
      }
      // Cascade keeps dependency locks honest; select-all turns requirements on
      // too, so nothing ends up stranded off.
      return cascade({ ...state, selected, memory: {} });
    }

    case 'SET_SEARCH':
      return { ...state, search: action.search };

    case 'TOGGLE_SCORING':
      return {
        ...state,
        scoring: { ...state.scoring, [action.phase]: !state.scoring[action.phase] },
      };

    case 'SET_INGEST_MODE':
      return { ...state, ingestMode: action.mode };

    case 'TOGGLE_OVERRIDE_CACHE':
      return { ...state, overrideCache: !state.overrideCache };

    case 'SET_NAME':
      return { ...state, name: action.name };

    default:
      return state;
  }
}

/** Same groups in the same order with the same attribute keys? Guards HYDRATE
 *  against a refetch resetting a selection the operator already made. */
function isSameTaxonomy(
  a: readonly EnrichmentAttributeGroup[],
  b: readonly EnrichmentAttributeGroup[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((group, i) => {
    const other = b[i];
    if (group.slug !== other?.slug) return false;
    if (group.attributes.length !== other.attributes.length) return false;
    return group.attributes.every((attr, j) => attr.key === other.attributes[j]?.key);
  });
}

/** Selected attribute keys, in taxonomy order. The payload's `attributes`. */
export function selectedAttributeKeys(state: EnrichmentRunPanelState): string[] {
  return state.groups.flatMap((group) =>
    group.attributes.filter((a) => state.selected[a.key]).map((a) => a.key),
  );
}
